import { type ReactElement } from 'react';
import type { SCAPIShopperProductsSchemas } from 'src/types';
import { useProductImages } from '@/hooks/product/use-product-images';
import { DynamicImage } from '@/components/dynamic-image';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import EventCoursePriceBox from './event-course-price-box';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import { PRODUCT_IMAGE_VIEW_TYPES } from '@/lib/product-image-view-types';

interface EventCourseHeroProps {
    product: SCAPIShopperProductsSchemas['Product'];
    eventOccurrences: SCAPIShopperProductsSchemas['Product'][];
}

/** Hero image spans full width; the booking box overlays the right edge on desktop. */
const HERO_IMAGE_WIDTHS: DynamicImageDimensions = { base: '100vw', lg: '100vw', '2xl': 1440 };

/** Figma neutral/10 — used for hero gradient overlays. */
const HERO_GRADIENT_COLOR = '#231917';

/** Mobile: solid from 27%, fades out by 54%. */
const MOBILE_HERO_GRADIENT = `linear-gradient(to bottom, rgba(35, 25, 23, 0.5) 27%, rgba(35, 25, 23, 0) 54%)`;

/** Desktop: layered gradients for title legibility (0→60% and 20%@80%→60%). */
const DESKTOP_HERO_GRADIENTS = [
    `linear-gradient(to left, ${HERO_GRADIENT_COLOR} 0%, rgba(35, 25, 23, 0) 100%)`,
    `linear-gradient(to top, rgba(35, 25, 23, 0.8) 20%, rgba(35, 25, 23, 0) 60%)`,
] as const;

/**
 * Hero section for event/course PDPs: the fondo-set still as background,
 * the event kicker + title overlaid at the bottom-left, and the booking box
 * ({@link EventCoursePriceBox}) overlapping the right edge on desktop.
 */
export default function EventCourseHero({ product, eventOccurrences }: EventCourseHeroProps): ReactElement {
    const { galleryImages } = useProductImages({ product, viewType: PRODUCT_IMAGE_VIEW_TYPES.SFS });
    const heroImage = galleryImages[0];
    const kicker = product.c_eventCategoryName || product.c_pimBrandName;

    return (
        <section className="relative w-full">
            <div className="grid-global !px-0 lg:items-end relative lg:aspect-video lg:pb-[32px] lg:flex flex-col lg:max-h-[648px]">
                <div className="relative col-span-full aspect-[4/3] sm:aspect-video w-full lg:absolute lg:top-0 lg:left-0 lg:right-0 lg:bottom-0 lg:max-h-[648px]">
                    {heroImage ? (
                        <DynamicImage
                            src={heroImage.src}
                            alt={resolveImageAlt({ alt: heroImage.alt, name: product.name })}
                            imageProps={{ className: 'w-full h-full' }}
                            className="absolute inset-0 h-full w-full object-cover object-center"
                            priority="high"
                            widths={HERO_IMAGE_WIDTHS}
                        />
                    ) : (
                        <div className="absolute inset-0 h-full w-full bg-sand-95" />
                    )}
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 lg:hidden"
                        style={{ background: MOBILE_HERO_GRADIENT }}
                    />
                    {DESKTOP_HERO_GRADIENTS.map((gradient) => (
                        <div
                            key={gradient}
                            aria-hidden
                            className="pointer-events-none absolute inset-0 hidden lg:block"
                            style={{ background: gradient }}
                        />
                    ))}
                </div>
                <div className="absolute top-0 pt-8 items-center text-center lg:text-left justify-start inset-x-0 bottom-0 flex flex-col gap-2 p-6 text-neutral-white lg:top-unset lg:justify-end lg:h-fit lg:top-unset lg:bottom-0 lg:relative lg:items-start lg:col-span-5 lg:col-start-2 lg:p-0">
                    {kicker && <p className="label-l uppercase text-brown-90">{kicker}</p>}
                    <h1 className="heading-display-l">{product.name}</h1>
                </div>

                {/* Booking box — 4 cols (8–11), 1 col margin on the right; overlays hero on desktop */}
                <div className="col-span-full z-10 -mt-2 px-0 lg:static lg:col-span-4 lg:col-start-8 lg:mt-0">
                    <EventCoursePriceBox product={product} eventOccurrences={eventOccurrences} />
                </div>
            </div>
        </section>
    );
}
